// /content/versions/:id/review (E31-S6-T3, first cut): one version under review, on its own page, as
// the versions list's Review and Open links reach it. The same pane the scripts page shows beside its
// list (review-pane.tsx); the plan supplies the title's name, runtimes and holds.
import type { Principal } from "@waypoint/contracts";
import { ForbiddenError, NotFoundError } from "../../api/errors.js";
import { t } from "../../i18n/t.js";
import { usePlan, useVersion } from "../../versions/queries.js";
import { EmptyState, Page } from "../page.js";
import { ReviewPane } from "./review-pane.js";
import { announcement, useAnnouncer } from "./scripts-page.js";
import { titleInfoOf } from "./script-format.js";

export function ReviewPage({ id, principal }: { id: string; principal: Principal }) {
  const version = useVersion(id);
  const plan = usePlan();
  const { say, region } = useAnnouncer();

  if (version.isPending)
    return (
      <Page title={t("scripts.title")}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("review.loading")}
        </p>
      </Page>
    );
  if (version.isError) {
    const err = version.error;
    return (
      <Page
        title={t(err instanceof ForbiddenError ? "page.forbidden.title" : "review.failed.title")}
      >
        <EmptyState
          icon={err instanceof ForbiddenError ? "lock" : "alert"}
          title={
            err instanceof ForbiddenError
              ? t("scripts.forbidden")
              : err instanceof NotFoundError
                ? t("review.notFound")
                : t("review.failed.line")
          }
        />
      </Page>
    );
  }

  const info = titleInfoOf(plan.data, version.data.titleId, version.data.pack.id);
  return (
    <section aria-labelledby="page-title" className="grid gap-4 pb-14">
      <ReviewPane
        version={version.data}
        info={info}
        principal={principal}
        onActed={(acted) => {
          say(announcement(acted, version.data.titleId));
        }}
        level={1}
      />
      {region}
    </section>
  );
}
